import {
    Listbox,
    Transition,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/react';
import moment from 'moment';
import React, { Fragment } from 'react';

import Divider from './Divider';
import Icon from './Icon';
import IconButton from './IconButton';

import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useProfiles from 'flow/hooks/useProfiles';

import { activateExtensionProfile } from 'flow/actions/extension-actions';
import {
    selectExtensionActiveProfile,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    getStandardProfileDescriptionInUserLanguage,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';

const PauseProfileButton = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const { profiles: ownProfiles, pauseProfiles } = useProfiles();

    const availableProfile = ownProfiles.find(
        (profile) => profile.get('plusId') === 0,
    );

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, ownExtensionKey),
    );

    const setPauseProfile = (profile: TypedMap<Profile> = null) => {
        if (!profile) {
            profile = pauseProfiles.first();
        }

        const until = resolveDefaultDuration(
            profile.get('defaultDuration'),
            moment(),
        );

        const DTO = profile
            .set('active', true)
            .set(
                'activeUntil',
                until ? until.format(ISO_8601_MS_RESOLUTION) : null,
            );

        dispatch(activateExtensionProfile(ownExtensionKey, DTO));
    };

    const removePauseProfile = () => {
        const DTO = availableProfile
            .set('active', true)
            .set('activeUntil', null);

        dispatch(activateExtensionProfile(ownExtensionKey, DTO));
    };

    const isActive = pauseProfiles
        .map((profile) => profile.get('key'))
        .toJS()
        .includes(activeProfile?.get('key'));

    if (!pauseProfiles?.size) {
        return null;
    }

    const hasMultipleProfiles = pauseProfiles.size > 1;

    // The profile's own name, the same word the picker shows, rather than a
    // string of our own that could drift from it.
    const pauseProfileName = getStandardProfileDescriptionInUserLanguage(
        pauseProfiles.first()?.get('description'),
        i18next,
    );

    return (
        <div className="flex">
            {hasMultipleProfiles && !isActive ? (
                <div
                    className={twMerge(
                        'me-px h-[40px] rounded-s-sm p-2',
                        isActive ? 'bg-main' : 'bg-gray-200',
                    )}
                >
                    <Icon
                        iconKey={'pause'}
                        className={isActive ? 'text-white' : 'text-icon'}
                    />
                </div>
            ) : (
                <IconButton
                    aria-label={pauseProfileName}
                    aria-pressed={isActive}
                    className={twMerge(
                        'hover:darken-20 relative p-2 focus-visible:z-10',
                        isActive ? 'bg-main' : 'bg-gray-200',
                        hasMultipleProfiles ? 'me-px rounded-e-none' : '',
                    )}
                    onClick={
                        isActive ? removePauseProfile : () => setPauseProfile()
                    }
                >
                    <Icon
                        iconKey={'pause'}
                        className={isActive ? 'text-white' : 'text-icon'}
                    />
                </IconButton>
            )}

            {hasMultipleProfiles && (
                <Listbox>
                    <div className="relative">
                        <ListboxButton className="focus-ring inline-flex h-[40px] max-w-[240px] items-center rounded-sm rounded-s-none bg-gray-200 hover:bg-gray-300">
                            <span className="sr-only">
                                {i18next.t('Tooltip.Nav.ProfilePicker')}
                            </span>

                            <div className="py-2">
                                <Icon iconKey="arrow_down" />
                            </div>
                        </ListboxButton>

                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="shadow-base absolute end-0 z-30 mt-1 flex w-[320px] flex-col gap-4 rounded-sm bg-white p-3 text-gray-800 dark:bg-gray-200">
                                <ListboxOptions className="flex max-h-[520px] origin-top-right flex-col gap-2 overflow-hidden outline-hidden rtl:origin-top-left">
                                    {pauseProfiles.map((profile) => (
                                        <ListboxOption
                                            key={profile.get('key')}
                                            onClick={() =>
                                                setPauseProfile(profile)
                                            }
                                            className={({ focus }) =>
                                                twMerge(
                                                    'group cursor-pointer rounded-sm p-2 select-none hover:bg-gray-200 dark:hover:bg-gray-300',
                                                    focus ? 'bg-gray-200' : '',
                                                )
                                            }
                                            value={profile}
                                        >
                                            {profile.get('description')}
                                        </ListboxOption>
                                    ))}

                                    {isActive && (
                                        <>
                                            <Divider className="my-0" />

                                            <ListboxOption
                                                key="clear-pause-profile"
                                                onClick={removePauseProfile}
                                                className={({ focus }) =>
                                                    twMerge(
                                                        'group cursor-pointer rounded-sm p-2 select-none hover:bg-gray-200',
                                                        focus
                                                            ? 'bg-gray-200'
                                                            : '',
                                                    )
                                                }
                                                value="unpause"
                                            >
                                                {i18next.t('profile.unpause')}
                                            </ListboxOption>
                                        </>
                                    )}
                                </ListboxOptions>
                            </div>
                        </Transition>
                    </div>
                </Listbox>
            )}
        </div>
    );
};

export default PauseProfileButton;
